import {
  useQuery, useMutation, useQueryClient, UseMutationResult, QueryFunction,
} from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import GenericDataProvider from '../../common/api/GenericDataProvider';
import { useGetModules } from '../../common/api/endpoints/modules/modules';
import { AdminModelMap, AdminObjectName } from '../common/DataColumnConfig';

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
}

export interface DataOptions<K extends AdminObjectName = AdminObjectName> {
  objectName: K;
  parentObjectName?: string;
  parentId?: string;
  customQueryKey?: string | unknown[];
  filter?: Record<string, unknown>;
  sort?: string;
  defaultSearchField?: string;
  searchableFields?: string[];
  searchText?: string | null;
  currentPage: number;
  currentPageSize: number;
}

export interface UseDataResult<T = unknown> {
  data: T[];
  isLoading: boolean;
  isPreviousData: boolean;
  error: unknown;
  pagination: Pagination;
  createMutation: UseMutationResult<unknown, unknown, Partial<T>>;
  updateMutation: UseMutationResult<unknown, unknown, Partial<T> & { id: string | number }>;
}

// Support for specialized data sources
const useModuleApprovalData = (options: Partial<DataOptions>) => {
  const { filter = {}, objectName } = options;
  const { t } = useTranslation();

  // Use the Orval-generated hook for modules with pending_approval state
  const { data, isLoading, error } = useGetModules(
    { state: 'pending_approval', ...filter },
    {
      query: {
        enabled: objectName === 'modules/approvals',
        select: (response: unknown) => {
          const resp = response as { data?: { data?: Record<string, unknown>[] } };
          const modules = resp?.data?.data || [];
          const transformedModules = modules.map((module) => {
            const stateTransitions = (module.stateTransitions || []) as {
              changedAt: string;
              toState: string;
              metadata?: { approvalType?: string };
              changedBy?: string
            }[];
            const transitionsCopy = [...stateTransitions];
            transitionsCopy.sort(
              (a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime(),
            );

            const pendingApprovalTransition = transitionsCopy.find(
              (transition) => transition.toState === 'pending_approval',
            );
            const approvalTypeLabel = t(
              `admin.workflow.approvalTypes.${pendingApprovalTransition?.metadata?.approvalType}`,
            );
            return {
              ...module,
              requestedAt: pendingApprovalTransition?.changedAt,
              requestedBy: pendingApprovalTransition?.changedBy,
              approvalTypeLabel,
            };
          });
          return transformedModules;
        },
      },
    },
  );

  return {
    data: (data as unknown as unknown[]) || [],
    pagination: { page: 1, pageSize: (data as unknown[])?.length || 0, total: (data as unknown[])?.length || 0 },
    isLoading,
    error,
  };
};

export const parseSearchText = ({
  defaultSearchField,
  searchableFields,
  searchText,
}: {
  defaultSearchField: string;
  searchableFields?: string[];
  searchText?: string | null;
}) => {
  // Initialize the structure to store search info
  const searchInfo: { filters: Record<string, string> } = {
    filters: {},
  };

  if (!searchText) return searchInfo;

  // Regular expression to match field:value pairs and standalone words
  const regex = /(\w+:"[^"]+"|\w+:[^\s]+|[^\s]+)/g;
  const parts = searchText.match(regex);

  if (!parts) return searchInfo;

  const defaultSearchTerms: string[] = [];

  parts.forEach((part) => {
    if (part.includes(':')) {
      const [field, value] = part.split(':');
      if (field && value && searchableFields?.includes(field)) {
        // Remove quotes from value if present
        searchInfo.filters[field] = value.replace(/^"|"$/g, '');
      } else {
        // Fix: if field is not searchable, treat the whole part as default search term
        defaultSearchTerms.push(part);
      }
    } else {
      defaultSearchTerms.push(part);
    }
  });

  // If there are any default search terms, add them to filters with defaultSearchField
  if (defaultSearchTerms.length > 0) {
    searchInfo.filters[defaultSearchField] = defaultSearchTerms.join(' ');
  }

  return searchInfo;
};

const EMPTY_ARRAY: unknown[] = [];

/**
 * Hook for fetching and managing admin data.
 * Supports pagination, filtering, searching, and mutations.
 */
const useData = <K extends AdminObjectName>({
  objectName,
  customQueryKey,
  filter = {},
  sort,
  defaultSearchField = 'title',
  searchableFields,
  searchText,
  currentPage,
  currentPageSize,
}: DataOptions<K>): UseDataResult<AdminModelMap[K]> => {
  const dataProvider = new GenericDataProvider();
  const queryClient = useQueryClient();

  const approvalData = useModuleApprovalData({
    objectName,
    filter,
    searchText,
    currentPage,
    currentPageSize,
  });

  const searchInfo = React.useMemo(
    () => parseSearchText({
      defaultSearchField,
      searchableFields,
      searchText,
    }),
    [defaultSearchField, searchableFields, searchText],
  );

  const fetchData = async ({ queryKey: key }: { queryKey: unknown[] }) => {
    const [, queryOptions] = key as [string, { page?: number; sort?: string }];
    const { page = 1, sort: sortVal } = queryOptions || {};
    const options = {
      page: page as number,
      pageSize: currentPageSize,
      sort: sortVal,
      filter: { ...filter, ...searchInfo?.filters },
      queryParams: {},
    };
    const result = await dataProvider.getList(objectName, options);
    if (result == null) {
      return {
        data: [],
        pagination: { page: options.page, pageSize: currentPageSize, total: 0 },
      };
    }
    return result;
  };

  const queryKey = React.useMemo(
    () => [
      customQueryKey || `/${objectName}`,
      {
        page: currentPage,
        pageSize: currentPageSize,
        search: searchText,
        filter,
        sort,
      },
    ],
    [customQueryKey, objectName, currentPage, currentPageSize, searchText, filter, sort],
  );

  const {
    data, isLoading, isPlaceholderData, error,
  } = useQuery({
    queryKey,
    queryFn: fetchData as unknown as QueryFunction<unknown, readonly unknown[]>,
    staleTime: 1000 * 30, // fetch fresh data after 30sec
    enabled: objectName !== 'modules/approvals',
    placeholderData: (prev) => prev,
  });

  const createMutation = useMutation({
    mutationFn: (newData: unknown) => dataProvider.create(objectName, newData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (newData: unknown) => dataProvider.update(objectName, (newData as { id?: string })?.id || '', newData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });
    },
  });

  const cached = queryClient.getQueryData(queryKey) as { pagination?: Pagination } | undefined;
  const dataAny = data as {
    pagination?: Pagination;
    data?: unknown | { data?: unknown[]; pagination?: Pagination }
  } | undefined;

  // Handle nested data and pagination in report structures
  const reportData = dataAny?.data && !Array.isArray(dataAny.data) ? (dataAny.data as { data?: unknown[] })?.data : undefined;
  const reportPagination = dataAny?.data && !Array.isArray(dataAny.data)
    ? (dataAny.data as { pagination?: Pagination })?.pagination
    : undefined;

  const paginationSource = dataAny?.pagination || reportPagination;
  const settledTotal = dataAny?.pagination?.total ?? reportPagination?.total;
  const safePagination = paginationSource
    && typeof settledTotal === 'number'
    && !isLoading
    && !isPlaceholderData
    ? paginationSource
    : cached?.pagination;

  const memoizedPagination = React.useMemo(
    () => safePagination || { page: currentPage, pageSize: currentPageSize, total: 0 },
    [safePagination, currentPage, currentPageSize],
  );

  if (objectName === 'modules/approvals') {
    return {
      ...approvalData,
      isPreviousData: false,
      createMutation: createMutation as unknown as UseMutationResult<unknown, unknown, Partial<AdminModelMap[K]>>,
      updateMutation: updateMutation as unknown as UseMutationResult<unknown, unknown, Partial<AdminModelMap[K]> & { id: string | number }>,
    } as UseDataResult<AdminModelMap[K]>;
  }

  return {
    data: (reportData || dataAny?.data || EMPTY_ARRAY) as AdminModelMap[K][],
    isLoading,
    isPreviousData: isPlaceholderData || false,
    error,
    pagination: memoizedPagination as Pagination,
    createMutation: createMutation as unknown as UseMutationResult<unknown, unknown, Partial<AdminModelMap[K]>>,
    updateMutation: updateMutation as unknown as UseMutationResult<unknown, unknown, Partial<AdminModelMap[K]> & { id: string | number }>,
  };
};

export default useData;
